import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
  TemplateRef,
  ApplicationRef,
  Injector,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { Subscription, filter, distinctUntilChanged } from 'rxjs';
import { Router } from '@angular/router';
import { SubscriptionService } from '../../../services/subscription.service';
import { UserService } from '../../../services/user.service';
import { CommonService } from '../../../services/common.service';
import { AlertService } from '../../../services/alert.service';
import { getApiErrorMessage } from '../../../utilities/api-error.util';

import { SubscriptionPlan, PromotionCode, ChangePreviewResponse } from '../../../models/subscription/subscription.model';
import { ClubService } from '../../../services/club.service';
import { Club } from '../../../models/club/club-info.model';
import { COUNTRY_CURRENCY } from '../subscription-labels';
import { PlanCardComponent } from '../plan-card/plan-card.component';
import { BillingToggleComponent } from '../billing-toggle/billing-toggle.component';
import { PaymentModalComponent } from '../payment-modal/payment-modal.component';

@Component({
  selector: 'cricteams-upgrade-modal',
  standalone: true,
  imports: [
    CommonModule,
    PlanCardComponent,
    BillingToggleComponent,
    PaymentModalComponent,
  ],
  templateUrl: './upgrade-modal.component.html',
})
export class UpgradeModalComponent implements OnInit, OnDestroy {
  @ViewChild('modalTemplate') modalTemplate!: TemplateRef<unknown>;
  @ViewChild('paymentModal') paymentModal!: PaymentModalComponent;

  isOpen = false;
  loading = false;
  clubId = '';
  clubCountry = 'US';
  currencyCode = 'USD';

  plans: SubscriptionPlan[] = [];
  promotionCodes: PromotionCode[] = [];
  currentPlanLevel = 1;
  selectedInterval: 'Monthly' | 'Yearly' = 'Monthly';
  savingsPercent = 0;

  // Payment modal state (Free → Paid)
  selectedUpgradePlan: SubscriptionPlan | null = null;
  selectedPriceId = '';
  upgradingPlanLevel: number | null = null;

  // Upgrade confirmation state (Paid → Higher Paid)
  showUpgradeConfirm = false;
  upgradePreview: ChangePreviewResponse | null = null;
  upgradeTarget: { plan: SubscriptionPlan; priceId: string } | null = null;
  confirmingUpgrade = false;
  changingPlanLevel: number | null = null;

  private isHeaderTriggered = false;

  private portalHost!: DomPortalOutlet;
  private activePortal: TemplatePortal<unknown> | null = null;
  private clubIdSub!: Subscription;

  constructor(
    private subscriptionService: SubscriptionService,
    private userService: UserService,
    private commonService: CommonService,
    private alertService: AlertService,
    private clubService: ClubService,
    private router: Router,
    private appRef: ApplicationRef,
    private injector: Injector,
    private viewContainerRef: ViewContainerRef,
  ) {}

  ngOnInit() {
    const container = document.createElement('div');
    document.body.appendChild(container);
    this.portalHost = new DomPortalOutlet(
      container,
      this.appRef,
      this.injector,
    );

    this.clubIdSub = this.userService.currentClubId$
      .pipe(filter(id => !!id), distinctUntilChanged())
      .subscribe((clubId) => {
        setTimeout(() => this.checkAndShow(clubId).catch(() => {}), 500);
      });
  }

  ngOnDestroy() {
    this.closeModal();
    this.portalHost?.dispose();
    this.clubIdSub?.unsubscribe();
  }

  private async checkAndShow(clubId: string) {
    // Only show for managers
    const isManager = await this.commonService.isManager();
    if (!isManager) return;

    // Check hasSelectedPlan from localStorage
    try {
      const userBaseInfo = JSON.parse(localStorage.getItem('userBaseInfo') ?? '{}');
      const clubs = userBaseInfo?.clubs ?? [];
      const club = clubs.find((c: Club) => c.id === clubId);
      if (!club?.subscription || club.subscription.hasSelectedPlan !== false) return;

      this.clubId = clubId;
      this.resolveClubInfo(club);
      this.currentPlanLevel = 1;

      await this.openModal();
    } catch {
      // ignore parse errors
    }
  }

  async showFromHeader() {
    this.isHeaderTriggered = true;
    this.clubId = localStorage.getItem('currentClubId') ?? '';
    if (!this.clubId) return;

    try {
      const userBaseInfo = JSON.parse(localStorage.getItem('userBaseInfo') ?? '{}');
      const club = (userBaseInfo?.clubs ?? []).find((c: Club) => c.id === this.clubId);
      this.resolveClubInfo(club);
      this.currentPlanLevel = club?.subscription?.planId === 'cricket-club-pro-tier' ? 2
        : club?.subscription?.planId === 'cricket-club-premium-tier' ? 3
        : 1;
    } catch {
      // defaults are fine
    }

    // Check for pending downgrade or cancellation — redirect to subscription settings instead
    const subscription = await this.subscriptionService.getClubSubscription(this.clubId);
    if (subscription?.pendingDowngradePlanId || subscription?.cancelAtPeriodEnd) {
      const endDate = subscription.currentPeriodEnd
        ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : '';
      const msg = subscription.pendingDowngradePlanId
        ? `Your plan is changing to ${subscription.pendingDowngradePlanDisplayName ?? 'a lower tier'} on ${endDate}. Manage your subscription in settings.`
        : `Your subscription is canceling on ${endDate}. Manage your subscription in settings.`;
      this.alertService.showError(msg);
      this.router.navigate(['/settings/subscription']);
      return;
    }

    // If already on highest plan, go to settings instead
    if (this.currentPlanLevel >= 3) {
      this.router.navigate(['/settings/subscription']);
      return;
    }

    await this.openModal();
  }

  private resolveClubInfo(club: Club | undefined) {
    this.clubCountry = club?.country ?? 'US';
    this.currencyCode = COUNTRY_CURRENCY[this.clubCountry] || 'USD';
  }

  private async openModal() {
    this.isOpen = true;
    this.loading = true;

    // Render via portal
    setTimeout(() => {
      if (this.modalTemplate) {
        this.activePortal = new TemplatePortal(this.modalTemplate, this.viewContainerRef);
        this.portalHost.attach(this.activePortal);
      }
    });

    try {
      const isFree = this.currentPlanLevel <= 1;
      const [plans, promoCodes] = await Promise.all([
        this.subscriptionService.getPlansByPersonaAndSport('Club', 'Cricket'),
        isFree ? this.subscriptionService.getPublicPromotionCodes() : Promise.resolve([]),
      ]);
      this.plans = (plans ?? []).sort((a, b) => a.level - b.level);
      this.promotionCodes = promoCodes;
      this.calculateSavings();
    } catch (err) {
      console.error('Failed to load subscription plans:', err);
    }
    this.loading = false;
  }

  onIntervalChange(interval: 'Monthly' | 'Yearly') {
    this.selectedInterval = interval;
  }

  async onUpgradeClick(event: { plan: SubscriptionPlan; priceId: string }) {
    if (this.upgradingPlanLevel || this.changingPlanLevel) return;

    if (this.currentPlanLevel <= 1) {
      // Free → Paid: use payment modal (subscribe flow)
      await this.onUpgradeViaPayment(event);
    } else {
      // Paid → Higher Paid: use change flow with confirmation dialog
      await this.showUpgradeConfirmation(event);
    }
  }

  private async onUpgradeViaPayment(event: { plan: SubscriptionPlan; priceId: string }) {
    this.upgradingPlanLevel = event.plan.level;

    this.selectedUpgradePlan = event.plan;
    this.selectedPriceId = event.priceId;

    const matchedPrice = event.plan.prices.find(p => p.id === event.priceId);

    this.paymentModal.plan = event.plan;
    this.paymentModal.priceId = event.priceId;
    this.paymentModal.priceAmount = matchedPrice?.amount ?? 0;
    this.paymentModal.clubId = this.clubId;
    this.paymentModal.selectedInterval = this.selectedInterval;
    this.paymentModal.selectedCountry = this.clubCountry;

    await this.paymentModal.open();
    this.upgradingPlanLevel = null;
  }

  private async showUpgradeConfirmation(event: { plan: SubscriptionPlan; priceId: string }) {
    this.changingPlanLevel = event.plan.level;
    this.upgradeTarget = event;

    const response = await this.subscriptionService.previewChange({
      clubId: this.clubId,
      priceId: event.priceId,
    });

    this.changingPlanLevel = null;

    if (response?.isSuccess && response.data) {
      // No payment method on file — redirect to subscription settings
      if (response.data.requiresPaymentMethod) {
        this.alertService.showError(
          'No payment method on file. Please go to Manage Billing to add a card, then try upgrading again.'
        );
        this.closeModal();
        this.router.navigate(['/settings/subscription']);
        return;
      }

      this.upgradePreview = response.data;
      this.showUpgradeConfirm = true;
    } else {
      this.alertService.showError(getApiErrorMessage(response, 'Failed to preview upgrade.'));
    }
  }

  closeUpgradeConfirm() {
    this.showUpgradeConfirm = false;
    this.upgradePreview = null;
    this.upgradeTarget = null;
  }

  async confirmUpgrade() {
    if (this.confirmingUpgrade || !this.upgradeTarget) return;
    this.confirmingUpgrade = true;

    const response = await this.subscriptionService.changeSubscription({
      clubId: this.clubId,
      priceId: this.upgradeTarget.priceId,
    });

    if (response?.isSuccess) {
      // No payment method on file (e.g., 100% promo user) — prompt to add card via Manage Billing
      if (response.data?.requiresPaymentMethod) {
        this.showUpgradeConfirm = false;
        this.upgradePreview = null;
        this.upgradeTarget = null;
        this.confirmingUpgrade = false;
        this.alertService.showError(
          'No payment method on file. Please go to Settings → Subscription → Manage Billing to add a card, then try upgrading again.'
        );
        this.closeModal();
        this.router.navigate(['/settings/subscription']);
        return;
      }

      this.alertService.showSuccess(response.message ?? 'Upgraded successfully!');
      this.showUpgradeConfirm = false;
      this.upgradePreview = null;
      this.upgradeTarget = null;

      // Refresh user info so header badge updates
      try {
        const userInfo = await this.userService.getUserInfo();
        if (userInfo) {
          localStorage.setItem('userBaseInfo', JSON.stringify(userInfo));
          this.clubService.isClubUpdated.next(true);
        }
      } catch { /* ignore */ }

      this.closeModal();
      this.isHeaderTriggered = false;
    } else {
      this.alertService.showError(getApiErrorMessage(response, 'Failed to upgrade.'));
    }

    this.confirmingUpgrade = false;
  }

  onSubscriptionChanged() {
    // Payment succeeded — close upgrade modal too
    this.closeModal();
    if (!this.isHeaderTriggered) {
      this.acknowledge();
    }
    this.isHeaderTriggered = false;
  }

  onPaymentClosed() {
    // Payment dismissed — upgrade modal stays open so user can pick a different plan
  }

  dismiss() {
    this.closeModal();
    if (!this.isHeaderTriggered) {
      this.acknowledge();
    }
    this.isHeaderTriggered = false;
  }

  private async acknowledge() {
    if (!this.clubId) return;
    await this.subscriptionService.acknowledgePlan(this.clubId);
    // Update localStorage so it doesn't show again
    try {
      const userBaseInfo = JSON.parse(localStorage.getItem('userBaseInfo') ?? '{}');
      const club = userBaseInfo?.clubs?.find((c: Club) => c.id === this.clubId);
      if (club?.subscription) {
        club.subscription.hasSelectedPlan = true;
        localStorage.setItem('userBaseInfo', JSON.stringify(userBaseInfo));
      }
    } catch { /* ignore */ }
  }

  closeModal() {
    this.isOpen = false;
    if (this.activePortal) {
      this.portalHost.detach();
      this.activePortal = null;
    }
  }

  private calculateSavings() {
    const proPlan = this.plans.find((p) => p.level === 2);
    if (!proPlan) {
      this.savingsPercent = 0;
      return;
    }

    const monthly = proPlan.prices.find(
      (p) => p.billingInterval === 'Monthly' && p.country === this.clubCountry,
    );
    const yearly = proPlan.prices.find(
      (p) => p.billingInterval === 'Yearly' && p.country === this.clubCountry,
    );

    if (monthly && yearly && monthly.amount > 0) {
      this.savingsPercent = Math.round(
        (1 - yearly.amount / (monthly.amount * 12)) * 100,
      );
    } else {
      this.savingsPercent = 0;
    }
  }

  /** Plans to display: hide tiers below the current plan */
  get displayPlans(): SubscriptionPlan[] {
    return this.plans.filter(p => p.level >= this.currentPlanLevel);
  }

  getCtaLabel(plan: SubscriptionPlan): string {
    if (plan.level === this.currentPlanLevel) return 'Current Plan';
    return `Upgrade to ${plan.displayName}`;
  }

  isCurrentPlan(plan: SubscriptionPlan): boolean {
    return plan.level === this.currentPlanLevel;
  }
}
